/* ============================================================================
   Slide 7 — "Brain Waves" — SHARED CONTENT (single source of truth)
   Read by both HTLDSlide7Inner (desktop) and HTLDSlide7Mobile. Edit copy ONLY
   here — English and German side by side; the German object must have exactly
   the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent7 = "#4fc3f7";

export interface Seg { t: string; b?: boolean; em?: boolean; }

export interface Slide7Wave { name: string; range: string; state: string; htld: boolean; }

const en = {
  meta: {
    breadcrumbPre: "States of Mind",
    breadcrumbAccent: "The frequency of consciousness",
    titlePlain: "Brain ", // trailing space: German joins "Gehirn"+"wellen"
    titleAccent: "Waves",
    subtitle: "Every state of mind has a measurable electrical signature",
    statesLabel: "The five states",
    calloutLabel: "The Theta gateway",
    badge: "Dreamfulness",
  },
  waves: [
    { name: "γ Gamma", range: "30–100 Hz", state: "Peak focus, high cognition, insight",          htld: false },
    { name: "β Beta",  range: "13–30 Hz",  state: "Alert, active thinking, problem solving",      htld: false },
    { name: "α Alpha", range: "8–13 Hz",   state: "Relaxed, calm, meditative — the bridge state", htld: false },
    { name: "θ Theta", range: "4–8 Hz",    state: "Deep meditation, REM sleep, dreaming",         htld: true  },
    { name: "δ Delta", range: "0.5–4 Hz",  state: "Deep dreamless sleep, physical restoration",   htld: false },
  ] as Slide7Wave[],
  callout: [
    { t: "Theta is the brain-wave pattern associated with the dreaming state. " },
    { t: "It is the window the Dreamfulness practice gently works with.", b: true },
  ] as Seg[],
  canvas: {
    /** Sleep-stage label per wave row (same order as `waves`). */
    sleep: ["Wakefulness", "Alert / Focus", "Stage 1", "REM Sleep", "Stage 3–4"],
    marker: "◀ Dreamfulness",
  },
};

export type Slide7Content = typeof en;

const de: Slide7Content = {
  meta: {
    breadcrumbPre: "Bewusstseinszustände",
    breadcrumbAccent: "Die Frequenz des Bewusstseins",
    titlePlain: "Gehirn",
    titleAccent: "wellen",
    subtitle: "Jeder Geisteszustand hat eine messbare elektrische Signatur",
    statesLabel: "Die fünf Zustände",
    calloutLabel: "Das Tor des Theta",
    badge: "Dreamfulness",
  },
  waves: [
    { name: "γ Gamma", range: "30–100 Hz", state: "Höchste Konzentration, starke Denkleistung, Einsicht", htld: false },
    { name: "β Beta",  range: "13–30 Hz",  state: "Wach, aktives Denken, Probleme lösen",                htld: false },
    { name: "α Alpha", range: "8–13 Hz",   state: "Entspannt, ruhig, meditativ – der Brückenzustand",    htld: false },
    { name: "θ Theta", range: "4–8 Hz",    state: "Tiefe Meditation, REM-Schlaf, Träumen",               htld: true  },
    { name: "δ Delta", range: "0,5–4 Hz",  state: "Tiefer, traumloser Schlaf, körperliche Erholung",     htld: false },
  ],
  callout: [
    { t: "Theta ist das Gehirnwellenmuster, das mit dem Träumen verbunden ist. " },
    { t: "Es ist das Fenster, mit dem die Dreamfulness-Praxis behutsam arbeitet.", b: true },
  ],
  canvas: {
    sleep: ["Wachzustand", "Wach / Fokus", "Phase 1", "REM-Schlaf", "Phase 3–4"],
    marker: "◀ Dreamfulness",
  },
};

export const slide7Content: ByLang<Slide7Content> = { en, de };
export const useSlide7 = () => useL(slide7Content);
